import React from 'react';

import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn, FieldValues, FieldPath } from 'react-hook-form';

import { InputWithInlineTitle } from 'src/registry/components/inputs/InputWithInlineTitle';
import {
    FormControl,
    FormControlStack,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';

type InputFormFieldProps<TData extends FieldValues> = {
    form: UseFormReturn<TData>;
    name: FieldPath<TData>;
    label?: string;
    type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
    className?: React.InputHTMLAttributes<HTMLInputElement>['className'];
    disabled?: boolean;
    inlineTitle?: string;
    pattern?: RegExp;
    placeholder?: string;
    required?: boolean;
    moreInfo?: string;
    min?: React.ComponentProps<'input'>['min'];
    variant?: 'dialog' | 'default';
    tooltip?: string;
    description?: string;
};

export default function InputFormField<TData extends FieldValues>({
    variant = 'dialog',
    required = true,
    form,
    name,
    label,
    type,
    className,
    disabled,
    inlineTitle,
    pattern,
    placeholder,
    moreInfo,
    min,
    tooltip,
    description,
}: InputFormFieldProps<TData>) {
    const typeIsNumber = type === 'number';

    const formPlaceholder = placeholder ?? (label ? `${label}...` : undefined);
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => {
                return (
                    <FormItem variant={variant}>
                        {label && (
                            <FormLabel variant={variant} required={required}>
                                {label}
                            </FormLabel>
                        )}
                        {tooltip && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger type="button" className="mb-0 ml-1 text-gray-400">
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-md">{tooltip}</TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        <FormControlStack>
                            <FormControl>
                                {inlineTitle ? (
                                    <InputWithInlineTitle
                                        {...field}
                                        inlineTitle={inlineTitle}
                                        className={className}
                                        // Bad input doesn't work good with type "number"
                                        type={typeIsNumber && pattern ? 'text' : type}
                                        min={typeIsNumber ? min : undefined}
                                        onChange={
                                            typeIsNumber
                                                ? (e) => {
                                                      const value = e.target.value;
                                                      if (
                                                          e.target.validity.badInput ||
                                                          (pattern && !pattern.test(value))
                                                      )
                                                          return;
                                                      field.onChange(value === '' ? null : Number(value));
                                                  }
                                                : (e) => {
                                                      const value = e.target.value;
                                                      if (pattern && !pattern.test(value)) return;
                                                      field.onChange(value);
                                                  }
                                        }
                                        value={field.value ?? ''}
                                        disabled={disabled}
                                        placeholder={formPlaceholder}
                                        moreInfo={moreInfo}
                                    />
                                ) : (
                                    <Input
                                        {...field}
                                        className={className}
                                        // Bad input doesn't work good with type "number"
                                        type={typeIsNumber && pattern ? 'text' : type}
                                        min={typeIsNumber ? min : undefined}
                                        onChange={
                                            typeIsNumber
                                                ? (e) => {
                                                      const value = e.target.value;
                                                      if (
                                                          e.target.validity.badInput ||
                                                          (pattern && !pattern.test(value))
                                                      )
                                                          return;
                                                      field.onChange(value === '' ? null : Number(value));
                                                  }
                                                : (e) => {
                                                      const value = e.target.value;

                                                      if (pattern && !pattern.test(value)) return;

                                                      field.onChange(value);
                                                  }
                                        }
                                        value={field.value ?? ''}
                                        disabled={disabled}
                                        placeholder={formPlaceholder}
                                        moreInfo={moreInfo}
                                    />
                                )}
                            </FormControl>
                            {description && <FormDescription>{description}</FormDescription>}
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                );
            }}
        />
    );
}
